import React, { useEffect, useMemo } from 'react';
import { MetaDataBoxContainer, CusSelect } from './styled';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import { EQUAL_ONLY_METADATA } from '@components/mapping-panel/constants/constant';
import { useMappingPanelStore } from '@components/mapping-panel/store';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import { EStringOp } from '@src/emp-3.0/types';
import { Button } from 'antd';
import RuleItem from '@src/emp-3.0/components/MappedEventBuilder/MetadataBox/RuleItem';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

const MetadataBox = ({ errorFields, index, data, onDelete, onChange }) => {
  const [needRefetchQueryKeys, setNeedRefetchQueryKeys] = useMappingPanelStore((state) => [
    state.needRefetchQueryKeys,
    state.setNeedRefetchQueryKeys,
  ]);

  const metadataKey = data?.[0]?.key;
  const { metadata: metadataList } = useInitialInfoStore();
  const {
    data: dimensionData,
    isFetching: isDimensionDataLoading,
    refetch: dimensionRefetch,
  } = useQueryDimensionData(
    {
      fields: metadataKey ? [metadataKey] : [],
      filters: [],
      options: {
        enabled: !!metadataKey,
      },
    },
    500,
  );

  useEffect(() => {
    if (metadataKey && needRefetchQueryKeys.includes(metadataKey)) {
      dimensionRefetch();
      setNeedRefetchQueryKeys(metadataKey, 'delete');
    }
  }, [metadataKey]);

  const metadataNameOptions = useMemo(
    () =>
      metadataList.map((canBeMappedDimension) => ({
        label: canBeMappedDimension?.display_name,
        value: canBeMappedDimension?.dimension_name,
      })),
    [metadataList],
  );

  const metadataName = metadataList.find(
    (dimension) => dimension?.dimension_name === metadataKey,
  )?.display_name;

  const isAllMetadataRulesEmpty =
    metadataName !== undefined && data?.every((rule) => rule.value.length === 0);

  const handleMetadataNameChange = (value) => {
    if (EQUAL_ONLY_METADATA.includes(value)) {
      data?.forEach((item) => {
        if (item.op !== EStringOp.EQ && item.op !== EStringOp.NOT_EQ) {
          item.op = EStringOp.EQ;
        }
        item.key = value;
      });
    } else {
      data?.forEach((item) => {
        item.key = value;
      });
    }
    onChange(index, data);
  };
  const addRuleItem = () => {
    const newRuleItem = {
      key: metadataKey,
      op: EStringOp.EQ,
      value: [],
    };
    data.push(newRuleItem);
    onChange?.(index, data);
  };

  return (
    <MetaDataBoxContainer>
      <div className="metadata-header">
        <span className="and-tag">AND</span>
        <span style={{ flexShrink: 0 }}>Metadata = </span>
        <span className="select-wapper">
          <CusSelect
            data-test-id={`metadata_name_${index + 1}.${index + 1}`}
            disabled={false}
            showSearch
            placeholder="Please select..."
            dropdownMatchSelectWidth={190}
            // placeholder is not displayed because metadataKey is initialized with '' instead of undefined.
            value={metadataKey || undefined}
            options={metadataNameOptions}
            onChange={handleMetadataNameChange}
            loading={false}
            onFocus={() => {}}
            onBlur={() => {}}
            onClick={(e) => e?.stopPropagation()}
            filterOption={(input, option) =>
              ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
            }
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </span>
        <span className={'delete-icon'}>
          <Button
            type="text"
            icon={<DeleteSvg />}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
          />
        </span>
      </div>
      {data?.map((item, subOrIndex) => {
        return (
          <RuleItem
            id={`metadata_rule_${index + 1}.${index + 1}.${subOrIndex + 1}`}
            key={`metadata_rule_${index + 1}.${index + 1}.${subOrIndex + 1}`}
            index={subOrIndex}
            isNoRules={isAllMetadataRulesEmpty}
            showLogicOperator={subOrIndex !== 0}
            metadataName={metadataName}
            data={item}
            options={dimensionData}
            loading={isDimensionDataLoading}
            errorFields={errorFields}
            onDelete={() => {
              const updatedData = [...data];
              updatedData.splice(subOrIndex, 1);
              onChange?.(index, updatedData);
            }}
            onChange={(value) => {
              const updatedData = [...data];
              updatedData[subOrIndex] = value;
              onChange?.(index, updatedData);
            }}
            disableDelete={subOrIndex === 0 && data.length === 1}
          />
        );
      })}
      <div className="add-rule">
        <span onClick={() => addRuleItem()}>Add OR Rule</span>
      </div>
    </MetaDataBoxContainer>
  );
};

export default MetadataBox;
